import { useState, useRef, useCallback } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Send, Users, Filter, Eye, CheckCircle, AlertCircle, Bold, Italic, Underline, Link, List, ListOrdered, Type, Code, Copy, Check } from 'lucide-react'
import { apiGet, apiPost } from '../lib/api'

// Available shortcodes for personalization
const SHORTCODES = [
  { code: '{{firstName}}', description: 'First name', example: 'John' },
  { code: '{{lastName}}', description: 'Last name', example: 'Smith' },
  { code: '{{fullName}}', description: 'Full name', example: 'John Smith' },
  { code: '{{email}}', description: 'Email address', example: 'john@example.com' },
  { code: '{{state}}', description: 'State abbreviation', example: 'TX' },
  { code: '{{location}}', description: 'Location/city', example: 'Austin' },
  { code: '{{profession}}', description: 'Primary profession', example: 'Paramedic' },
  { code: '{{professions}}', description: 'All professions', example: 'Paramedic, EMT' },
]

interface FilterCredentialType {
  id: number
  name: string
  abbreviation: string | null
}

interface FilterProfession {
  id: number
  name: string
  category: string | null
}

interface EmailFilters {
  credentialTypes: number[]
  credentialTypeOperator: 'OR' | 'AND' | 'NOT'
  professions: number[]
  professionOperator: 'OR' | 'AND' | 'NOT'
  certificationStatus: 'all' | 'active' | 'expiring_soon' | 'expired'
}

// Rich text editor toolbar button
function ToolbarButton({ onClick, active, children, title }: { 
  onClick: () => void
  active?: boolean
  children: React.ReactNode
  title: string 
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-2 rounded hover:bg-gray-200 transition-colors ${active ? 'bg-gray-200 text-blue-600' : 'text-gray-600'}`}
    >
      {children}
    </button>
  )
}

export default function EmailPage() {
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [linkText, setLinkText] = useState('')
  const [copiedCode, setCopiedCode] = useState<string | null>(null)
  const editorRef = useRef<HTMLDivElement>(null)
  const [filters, setFilters] = useState<EmailFilters>({
    credentialTypes: [],
    credentialTypeOperator: 'OR',
    professions: [],
    professionOperator: 'OR',
    certificationStatus: 'all',
  })
  const [showFilters, setShowFilters] = useState(false)
  const [sendSuccess, setSendSuccess] = useState(false)

  const { data: credentialTypesData } = useQuery<FilterCredentialType[]>({
    queryKey: ['/api/admin/email-filters/credential-types'],
  })

  const { data: professionsData } = useQuery<FilterProfession[]>({
    queryKey: ['/api/admin/email-filters/professions'],
  })

  const previewMutation = useMutation({
    mutationFn: () => apiPost<{ count: number }>('/admin/email-filters/count', { 
      credentialTypeIds: filters.credentialTypes,
      professionIds: filters.professions,
      credentialTypeOperator: filters.credentialTypeOperator,
      professionOperator: filters.professionOperator,
      certificationStatus: filters.certificationStatus,
    }),
  })

  const sendMutation = useMutation({
    mutationFn: async () => {
      const plainText = editorRef.current?.innerText || ''
      const response = await apiPost<{ message: string; recipientCount: number; failCount: number }>('/admin/send-email', { 
        subject, 
        htmlBody: message,
        textBody: plainText,
        recipientFilter: 'all',
        credentialTypeIds: filters.credentialTypes,
        professionIds: filters.professions,
        credentialTypeOperator: filters.credentialTypeOperator,
        professionOperator: filters.professionOperator,
        certificationStatus: filters.certificationStatus,
      })
      return response
    },
    onSuccess: (data) => {
      setSendSuccess(true)
      setSubject('')
      setMessage('')
      if (editorRef.current) {
        editorRef.current.innerHTML = ''
      }
      console.log('Email send success:', data)
      setTimeout(() => setSendSuccess(false), 5000)
    },
    onError: (error) => {
      console.error('Email send error:', error)
    },
  })

  const credentialTypes = credentialTypesData || []
  const professions = professionsData || []

  const handlePreview = () => {
    previewMutation.mutate()
  }

  const handleSend = () => {
    if (!subject.trim() || !message.trim()) {
      alert('Please enter both subject and message')
      return
    }
    if (window.confirm('Are you sure you want to send this email to the selected recipients?')) {
      sendMutation.mutate()
    }
  }

  const toggleCredentialType = (id: number) => {
    setFilters(prev => ({
      ...prev,
      credentialTypes: prev.credentialTypes.includes(id)
        ? prev.credentialTypes.filter(t => t !== id)
        : [...prev.credentialTypes, id]
    }))
  }

  const toggleProfession = (id: number) => {
    setFilters(prev => ({
      ...prev,
      professions: prev.professions.includes(id)
        ? prev.professions.filter(p => p !== id)
        : [...prev.professions, id]
    }))
  }

  // Rich text formatting functions
  const execCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value)
    editorRef.current?.focus()
    // Update message state with current HTML
    if (editorRef.current) {
      setMessage(editorRef.current.innerHTML)
    }
  }, [])

  const handleFormat = useCallback((format: 'bold' | 'italic' | 'underline') => {
    execCommand(format)
  }, [execCommand])

  const handleHeading = useCallback(() => {
    execCommand('formatBlock', 'h2')
  }, [execCommand])

  const handleList = useCallback((ordered: boolean) => {
    execCommand(ordered ? 'insertOrderedList' : 'insertUnorderedList')
  }, [execCommand])

  const handleInsertLink = useCallback(() => {
    const selection = window.getSelection()
    if (selection && selection.toString()) {
      setLinkText(selection.toString())
    } else {
      setLinkText('')
    }
    setLinkUrl('')
    setShowLinkModal(true)
  }, [])

  const confirmInsertLink = useCallback(() => {
    if (!linkUrl) return
    
    const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`
    
    if (linkText) {
      // Insert link with text
      const link = `<a href="${url}" style="color: #2563eb; text-decoration: underline;">${linkText}</a>`
      execCommand('insertHTML', link)
    } else {
      // Wrap selected text with link
      execCommand('createLink', url)
    }
    
    setShowLinkModal(false)
    setLinkUrl('')
    setLinkText('')
  }, [linkUrl, linkText, execCommand])

  const handleEditorInput = useCallback(() => {
    if (editorRef.current) {
      setMessage(editorRef.current.innerHTML)
    }
  }, [])

  const getPlainText = useCallback(() => {
    if (!editorRef.current) return ''
    return editorRef.current.innerText || ''
  }, [])

  const insertShortcode = useCallback((code: string) => {
    execCommand('insertText', code)
    setCopiedCode(code)
    setTimeout(() => setCopiedCode(null), 1500)
  }, [execCommand])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Send Email</h1>
        <p className="text-gray-500 mt-1">Compose and send announcements to users</p>
      </div>

      {/* Success Message */}
      {sendSuccess && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <p className="text-green-700">Email sent successfully!</p>
        </div>
      )}

      {/* Error Message */}
      {sendMutation.error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">
            {sendMutation.error instanceof Error ? sendMutation.error.message : 'Failed to send email'}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose Form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Compose Email</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter email subject..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                
                {/* Formatting Toolbar */}
                <div className="flex items-center gap-1 p-2 border border-gray-300 border-b-0 rounded-t-lg bg-gray-50">
                  <ToolbarButton onClick={() => handleFormat('bold')} title="Bold (Ctrl+B)">
                    <Bold className="w-4 h-4" />
                  </ToolbarButton>
                  <ToolbarButton onClick={() => handleFormat('italic')} title="Italic (Ctrl+I)">
                    <Italic className="w-4 h-4" />
                  </ToolbarButton>
                  <ToolbarButton onClick={() => handleFormat('underline')} title="Underline (Ctrl+U)">
                    <Underline className="w-4 h-4" />
                  </ToolbarButton>
                  
                  <div className="w-px h-6 bg-gray-300 mx-1" />
                  
                  <ToolbarButton onClick={handleHeading} title="Heading">
                    <Type className="w-4 h-4" />
                  </ToolbarButton>
                  
                  <div className="w-px h-6 bg-gray-300 mx-1" />
                  
                  <ToolbarButton onClick={() => handleList(false)} title="Bullet List">
                    <List className="w-4 h-4" />
                  </ToolbarButton>
                  <ToolbarButton onClick={() => handleList(true)} title="Numbered List">
                    <ListOrdered className="w-4 h-4" />
                  </ToolbarButton>
                  
                  <div className="w-px h-6 bg-gray-300 mx-1" />
                  
                  <ToolbarButton onClick={handleInsertLink} title="Insert Link">
                    <Link className="w-4 h-4" />
                  </ToolbarButton>
                </div>
                
                {/* Editable Content Area */}
                <div
                  ref={editorRef}
                  contentEditable
                  onInput={handleEditorInput}
                  className="w-full px-3 py-2 border border-gray-300 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[200px] bg-white text-gray-900 overflow-y-auto"
                  style={{ minHeight: '200px' }}
                  data-placeholder="Enter your message..."
                />
                <style>{`
                  [contenteditable]:empty:before {
                    content: attr(data-placeholder);
                    color: #9ca3af;
                    pointer-events: none;
                  }
                  [contenteditable] a {
                    color: #2563eb;
                    text-decoration: underline;
                  }
                `}</style>
              </div>

              {/* Shortcodes Reference */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <Code className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Personalization Shortcodes</span>
                  <span className="text-xs text-gray-400">(click to insert)</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {SHORTCODES.map((shortcode) => (
                    <button
                      key={shortcode.code}
                      onClick={() => insertShortcode(shortcode.code)}
                      title={`${shortcode.description} (e.g., ${shortcode.example})`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-mono bg-white border border-gray-200 text-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
                    >
                      {copiedCode === shortcode.code ? (
                        <>
                          <Check className="w-3 h-3 text-green-500" />
                          <span>Inserted!</span>
                        </>
                      ) : (
                        shortcode.code
                      )}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  These will be replaced with each recipient's actual data when the email is sent.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handlePreview}
              disabled={previewMutation.isPending}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1"
            >
              <Eye className="w-4 h-4 mr-2" />
              {previewMutation.isPending ? 'Loading...' : 'Preview Recipients'}
            </button>
            <button
              onClick={handleSend}
              disabled={sendMutation.isPending || !subject.trim() || !message.trim()}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1"
            >
              <Send className="w-4 h-4 mr-2" />
              {sendMutation.isPending ? 'Sending...' : 'Send Email'}
            </button>
          </div>

          {/* Preview Result */}
          {previewMutation.data && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 bg-blue-50 border-blue-200">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                <p className="text-blue-700">
                  <strong>{previewMutation.data.count}</strong> recipients match your filters
                </p>
              </div>
              <p className="text-xs text-blue-600 mt-1">Only users with verified email addresses are included</p>
            </div>
          )}
        </div>

        {/* Filters Panel */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-blue-600 text-sm font-medium"
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
          </div>

          {showFilters && (
            <div className="space-y-6">
              {/* Certification Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Certification Status
                </label>
                <select
                  value={filters.certificationStatus}
                  onChange={(e) => setFilters(prev => ({ ...prev, certificationStatus: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Users</option>
                  <option value="active">Active Certifications</option>
                  <option value="expiring_soon">Expiring Soon (30 days)</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              {/* Credential Types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Credential Types
                </label>
                <select
                  value={filters.credentialTypeOperator}
                  onChange={(e) => setFilters(prev => ({ ...prev, credentialTypeOperator: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                >
                  <option value="OR">ANY of these</option>
                  <option value="AND">ALL of these</option>
                  <option value="NOT">NONE of these</option>
                </select>
                <div className="max-h-40 overflow-y-auto space-y-1 border rounded-lg p-2">
                  {credentialTypes.length === 0 ? (
                    <p className="text-sm text-gray-500 p-2">No credential types available</p>
                  ) : credentialTypes.map(type => (
                    <label key={type.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={filters.credentialTypes.includes(type.id)}
                        onChange={() => toggleCredentialType(type.id)}
                        className="rounded border-gray-300"
                      />
                      {type.name}
                    </label>
                  ))}
                </div>
              </div>

              {/* Professions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Professions
                </label>
                <select
                  value={filters.professionOperator}
                  onChange={(e) => setFilters(prev => ({ ...prev, professionOperator: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                >
                  <option value="OR">ANY of these</option>
                  <option value="AND">ALL of these</option>
                  <option value="NOT">NONE of these</option>
                </select>
                <div className="max-h-40 overflow-y-auto space-y-1 border rounded-lg p-2">
                  {professions.length === 0 ? (
                    <p className="text-sm text-gray-500 p-2">No professions available</p>
                  ) : professions.map(prof => (
                    <label key={prof.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={filters.professions.includes(prof.id)}
                        onChange={() => toggleProfession(prof.id)}
                        className="rounded border-gray-300"
                      />
                      {prof.name}
                    </label>
                  ))}
                </div>
              </div>

              {/* Clear Filters */}
              <button
                onClick={() => setFilters({
                  credentialTypes: [],
                  credentialTypeOperator: 'OR',
                  professions: [],
                  professionOperator: 'OR',
                  certificationStatus: 'all',
                })}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear all filters
              </button>
            </div>
          )}

          {!showFilters && (
            <p className="text-sm text-gray-500">
              Click "Show Filters" to filter recipients by credential types, professions, or certification status.
            </p>
          )}
        </div>
      </div>

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Insert Link</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link Text {linkText && <span className="text-gray-400">(optional - will use selected text)</span>}
                </label>
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Display text for the link"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://example.com"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowLinkModal(false)
                  setLinkUrl('')
                  setLinkText('')
                }}
                className="flex-1 px-4 py-2 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmInsertLink}
                disabled={!linkUrl}
                className="flex-1 px-4 py-2 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Insert Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
